import React from 'react';
import { Book, Volume2, ArrowRight, Sparkles, Star, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useSpellingsData } from '@/hooks/useSpellingsData';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const SpellingOfTheDay = React.memo(() => {
  const { spellings, spellingOfTheDay, loading } = useSpellingsData();
  const navigate = useNavigate();

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-48 mx-auto mb-4"></div>
          <div className="bg-muted rounded-3xl h-[480px] max-w-3xl mx-auto"></div>
        </div>
      </section>
    );
  }

  const displaySpelling = spellingOfTheDay?.spelling || spellings[0];
  if (!displaySpelling) return null;

  const handleClick = () => navigate('/spellings');

  const playPronunciation = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(displaySpelling.word);
      u.rate = 0.75;
      u.pitch = 1.15;
      window.speechSynthesis.speak(u);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "name": `Word of the Day: ${displaySpelling.word}`,
    "description": displaySpelling.meaning,
    "educationalLevel": displaySpelling.difficulty_level,
    "learningResourceType": "Vocabulary",
    "teaches": displaySpelling.word,
    "inLanguage": "en",
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <section className="container mx-auto px-4 py-4 relative">
        {/* Title */}
        <div className="text-center mb-3 relative z-10">
          <div className="inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
            <h2 className="text-xl md:text-2xl font-fredoka font-black bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
              Word of the Day! ✨
            </h2>
            <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
          </div>
        </div>

        <Card
          onClick={handleClick}
          className="max-w-3xl mx-auto cursor-pointer rounded-[2rem] border-4 border-white shadow-2xl overflow-hidden relative bg-gradient-to-br from-purple-100 via-purple-200 to-pink-200 hover:scale-[1.01] transition-transform duration-300"
        >
          {/* Decorative blobs - mono purple */}
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-purple-300/50 rounded-full blur-2xl pointer-events-none animate-pulse" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-pink-300/50 rounded-full blur-2xl pointer-events-none animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-3 right-5 text-2xl pointer-events-none animate-bounce" style={{ animationDuration: '2.5s' }}>🌟</div>
          <div className="absolute bottom-3 left-5 text-2xl pointer-events-none animate-bounce" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>🎈</div>

          <CardContent className="p-5 md:p-6 relative z-10">
            {/* Top: Big word centered */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/90 shadow-sm">
                <Book className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-[11px] font-fredoka font-bold text-purple-700 uppercase tracking-wider">Today's Word</span>
              </div>

              <h3 className="text-4xl md:text-5xl font-fredoka font-black bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 bg-clip-text text-transparent leading-tight mb-1 animate-fade-in">
                {displaySpelling.word}
              </h3>

              {displaySpelling.pronunciation && (
                <p className="text-xs md:text-sm text-purple-500/80 font-fredoka mb-2">
                  /{displaySpelling.pronunciation}/
                </p>
              )}

              <div className="flex items-center justify-center gap-1.5 mb-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[11px] font-fredoka font-bold shadow-md">
                  ✨ {displaySpelling.difficulty_level}
                </span>
                {displaySpelling.category && (
                  <span className="px-2.5 py-0.5 rounded-full bg-white text-purple-700 text-[11px] font-fredoka font-bold border-2 border-purple-200 shadow-sm">
                    📚 {displaySpelling.category}
                  </span>
                )}
              </div>
            </div>

            {/* Info cards - 2 column grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-3">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-3 border-2 border-purple-200 shadow-sm">
                <p className="text-[11px] font-fredoka font-bold text-purple-800 mb-1 flex items-center gap-1">
                  <Star size={12} className="fill-purple-500 text-purple-500" />
                  WHAT IT MEANS
                </p>
                <p className="text-sm font-fredoka text-gray-800 leading-snug line-clamp-3">
                  {displaySpelling.meaning}
                </p>
              </div>

              {displaySpelling.example_sentence && (
                <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-3 border-2 border-pink-200 shadow-sm">
                  <p className="text-[11px] font-fredoka font-bold text-pink-800 mb-1 flex items-center gap-1">
                    <Lightbulb size={12} /> USE IT LIKE THIS
                  </p>
                  <p className="text-sm font-fredoka italic text-gray-800 leading-snug line-clamp-3">
                    "{displaySpelling.example_sentence}"
                  </p>
                </div>
              )}
            </div>

            {/* Synonyms / Antonyms */}
            {((displaySpelling.synonyms && displaySpelling.synonyms.length > 0) ||
              (displaySpelling.antonyms && displaySpelling.antonyms.length > 0)) && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {displaySpelling.synonyms && displaySpelling.synonyms.length > 0 && (
                  <div className="bg-gradient-to-br from-purple-100 to-fuchsia-100 rounded-xl p-2 border-2 border-purple-200">
                    <p className="text-[10px] font-fredoka font-bold text-purple-800 mb-1">😊 Same</p>
                    <div className="flex flex-wrap gap-1">
                      {displaySpelling.synonyms.slice(0, 3).map((s, i) => (
                        <span key={i} className="text-[11px] font-fredoka bg-white text-purple-700 px-2 py-0.5 rounded-full shadow-sm">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {displaySpelling.antonyms && displaySpelling.antonyms.length > 0 && (
                  <div className="bg-gradient-to-br from-pink-100 to-rose-100 rounded-xl p-2 border-2 border-pink-200">
                    <p className="text-[10px] font-fredoka font-bold text-pink-800 mb-1">🙃 Opposite</p>
                    <div className="flex flex-wrap gap-1">
                      {displaySpelling.antonyms.slice(0, 3).map((a, i) => (
                        <span key={i} className="text-[11px] font-fredoka bg-white text-pink-700 px-2 py-0.5 rounded-full shadow-sm">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); playPronunciation(); }}
                className="flex-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white font-fredoka font-bold text-sm py-4 shadow-lg border-2 border-white"
              >
                <Volume2 className="mr-2 h-4 w-4" />
                Hear It!
              </Button>
              <Button
                onClick={handleClick}
                className="flex-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:opacity-90 text-white font-fredoka font-bold text-sm py-4 shadow-lg border-2 border-white"
              >
                Explore More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </>
  );
});

SpellingOfTheDay.displayName = "SpellingOfTheDay";

export default SpellingOfTheDay;
